import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  useToast,
  Link,
  Modal,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  Spinner,
  ModalContent,
  ModalHeader,
  Accordion,
  AccordionItem,
  AccordionButton,
  Box,
  AccordionIcon,
  AccordionPanel,
  Textarea,
  FormHelperText,
} from '@chakra-ui/react';
import {
  GlobalPromoMediaType,
  PromoAutomation,
  PromoAutomationEventType,
  PromoAutomationTemplate,
  Schedule,
  SocialConnectedProfile,
  PromoAutomationOtherDestination,
  TemplateNamespaceMapping,
} from 'src/api/v1-api';
import { Controller, useForm } from 'react-hook-form';
import * as _ from 'lodash';
import { DAILY_OPTION, DAYS_OPTIONS, TIME_OPTIONS } from 'src/utils/common';
import {
  INSTAGRAM_POST_NETWORK_TYPES,
  FACEBOOK_POST_NETWORK_TYPES,
  YOUTUBE_POST_NETWORK_TYPES,
  getMarketplaceUrl,
} from 'src/utils/marketplaceUtils';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';
import PromoAutomationTemplateForm from './PromoAutomationTemplateForm';
import CategorySelector from 'src/components/molecules/category-selector';
import DynamicFieldsGuide from './DynamicFieldsGuide';
import InfoTooltip from 'src/components/atoms/info-tooltip';

interface PromoAutomationFormProps {
  promoAutomation: Partial<PromoAutomation> | null;
  globalPromoMediaTypes: GlobalPromoMediaType[];
  globalPromoMediaTypeNamespaceMapping: TemplateNamespaceMapping | undefined;
  connectedSocialProfiles: SocialConnectedProfile[];
  connectedSocialProfilesFetched?: boolean;
  onCancel: () => void;
  onUpdatePromoAutomation: (promoAutomation: Partial<PromoAutomation>) => void;
  onCreatePromoAutomation: (promoAutomation: Partial<PromoAutomation>) => void;
  isCategoryEditable?: boolean;
}

enum AutomationEventType {
  CRON_SOCIAL_NETWORK = 'Scheduled',
  EVERY_LISTING_PUBLICATION = 'Created with Every New Listing',
}

interface PromoAutomationFields {
  automationEventType: PromoAutomationEventType;
  globalPromoMediaTypeId: string;
  generatePromoMediaForNewListings: boolean;
  networks: string[];
  otherDestinations: PromoAutomationOtherDestination[];
  time: string;
  days: string[];
  enableAutomation: boolean;
  allowShareDuplicates: boolean;
  useStaticImage: boolean;
  promoImageUrl: string;
  captionText: string;
  automaticallyShareToNetworks: boolean;
  templates: Partial<PromoAutomationTemplate>[];
  entity_id: number | null;
  nickname: string;
  textOnly: boolean;
  title: string;
  filteredCategoryUrl: string;
}

const defaultSchedule: Schedule = {
  time: '1pm ET, 10am PT',
  days: [DAILY_OPTION],
};

export const MULTI_TEMPLATE_NETWORKS = ['instagram', 'facebook'];

const PromoAutomationForm = React.forwardRef(function PromoAutomationForm(
  {
    promoAutomation,
    globalPromoMediaTypes,
    globalPromoMediaTypeNamespaceMapping,
    connectedSocialProfiles,
    connectedSocialProfilesFetched = false,
    onCancel,
    onUpdatePromoAutomation,
    onCreatePromoAutomation,
    isCategoryEditable = false,
  }: PromoAutomationFormProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [filteredGlobalPromoMediaTypes, setFilteredGlobalPromoMediaTypes] = useState<
    GlobalPromoMediaType[]
  >([]);
  const [customSchedule, setCustomSchedule] = useState<boolean>(false);
  const [instagramPostType, setInstagramPostType] = useState<string>('');
  const [facebookPostType, setFacebookPostType] = useState<string>('');
  const [youtubePostType, setYoutubePostType] = useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [isVideoPostType, setIsVideoPostType] = useState<boolean>(false);
  const [previewPromoMediaData, setPreviewPromoMediaData] = useState<{
    image_url: string;
    caption: string;
  } | null>(null);
  const [filteredOtherDestinations, setFilteredOtherDestinations] = useState<
    PromoAutomationOtherDestination[]
  >([]);
  const [filteredUrlInput, setFilteredUrlInput] = useState<string>('');

  const { api } = useRequest();
  const { marketplace, categories } = useMarketplaceContext();
  const toast = useToast();

  const newEmptyTemplate: Partial<PromoAutomationTemplate> = {
    use_static_image: false,
    promo_image_url: '',
    global_promo_media_type_id: globalPromoMediaTypes[0]?.id,
    template_values: {},
  };

  const defaultValues = {
    automationEventType: PromoAutomationEventType.CRON_SOCIAL_NETWORK,
    networks: [],
    otherDestinations: [],
    time: defaultSchedule.time,
    days: defaultSchedule.days,
    enableAutomation: true,
    allowShareDuplicates: false,
    captionText: '',
    automaticallyShareToNetworks: true,
    templates: [newEmptyTemplate],
    entity_id: null,
    nickname: '',
    textOnly: false,
    title: '',
    filteredCategoryUrl: '',
  };
  const { control, handleSubmit, watch, setValue, getValues } = useForm<PromoAutomationFields>({
    defaultValues: defaultValues,
  });

  const automationEventTypeSelected = watch(
    'automationEventType',
    PromoAutomationEventType.CRON_SOCIAL_NETWORK,
  );
  const isSharingToNetworksAutomatically = watch('automaticallyShareToNetworks', true);
  const daysSelected = watch('days', defaultSchedule.days);
  const selectedNetworks = watch('networks', []);
  const templates = watch('templates', [newEmptyTemplate]);
  const otherDestinations = watch('otherDestinations', []);
  const entityId = watch('entity_id', null);
  const textOnly = watch('textOnly', false);
  const filteredCategoryUrl = watch('filteredCategoryUrl', '');

  const isMultiTemplate = MULTI_TEMPLATE_NETWORKS.some((network) =>
    selectedNetworks.includes(network),
  );

  const parseAndApplyFilteredUrl = (inputUrl: string) => {
    if (!inputUrl || typeof inputUrl !== 'string') return;

    let parsedCategoryId: number | null = null;

    if (inputUrl.includes('/category/')) {
      const categoryAndRest = inputUrl.split('/category/')[1];
      parsedCategoryId = categoryAndRest.includes('?')
        ? Number(categoryAndRest.split('?')[0])
        : Number(categoryAndRest);
    } else if (inputUrl.includes('?') && inputUrl.includes('filters=')) {
      parsedCategoryId = categories?.find((category) => category.parent === null)?.id ?? null;
    }

    const filtersAndRest = inputUrl.includes('filters=') ? inputUrl.split('filters=')[1] : '';
    const encodedFilters = filtersAndRest.includes('&')
      ? filtersAndRest.split('&')[0]
      : filtersAndRest;
    const parsedFilters = decodeURIComponent(encodedFilters);

    if (parsedFilters) {
      setValue('filteredCategoryUrl', parsedFilters);
    }

    if (parsedCategoryId && !isNaN(parsedCategoryId) && parsedCategoryId !== 0) {
      setValue('entity_id', parsedCategoryId);
    }

    setFilteredUrlInput('');
  };

  useEffect(() => {
    const initial = promoAutomation?.filtered_category_url ?? '';
    const looksLikeFullUrl =
      typeof initial === 'string' &&
      (initial.includes('/category/') || (initial.includes('?') && initial.includes('filters=')));
    if (looksLikeFullUrl) {
      parseAndApplyFilteredUrl(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoAutomation, categories]);

  useEffect(() => {
    if (isMultiTemplate && templates.length > 1) {
      setValue(
        'networks',
        selectedNetworks.filter((network) => MULTI_TEMPLATE_NETWORKS.includes(network)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const setInstagramSubNetworks = (networks: string[]) => {
    const instagramSelection = promoAutomation?.networks?.find((network) =>
      network.match(/instagram/),
    );
    if (instagramSelection && networks.includes('instagram')) {
      const postType = instagramSelection.split(':')[1] ?? 'stories';
      setInstagramPostType(postType);
    } else {
      setInstagramPostType('');
    }
  };

  const setFacebookSubnetworks = (networks: string[]) => {
    const facebookSelection = promoAutomation?.networks?.find((network) =>
      network.match(/facebook/),
    );
    if (facebookSelection && networks.includes('facebook')) {
      const postType = facebookSelection.split(':')[1] ?? 'post';
      setFacebookPostType(postType);
    } else {
      setFacebookPostType('');
    }
  };

  const setYoutubeSubnetworks = (networks: string[]) => {
    const youtubeSelection = promoAutomation?.networks?.find((network) => network.match(/youtube/));
    if (youtubeSelection && networks.includes('youtube')) {
      const postType = youtubeSelection.split(':')[1] ?? 'post';
      setYoutubePostType(postType);
    } else {
      setYoutubePostType('');
    }
  };

  useEffect(() => {
    setValue(
      'automationEventType',
      promoAutomation?.automation_event_type ?? PromoAutomationEventType.CRON_SOCIAL_NETWORK,
    );
    const promoAutomationNetworks = (promoAutomation?.networks ?? []).map(
      (network) => network.split(':')[0],
    );
    const connectedNetworks = connectedSocialProfiles.map((profile) => profile.platform);
    const networks = connectedNetworks.filter((network) =>
      promoAutomationNetworks.includes(network),
    );
    setValue('networks', networks);
    setValue('otherDestinations', promoAutomation?.other_destinations ?? []);
    setValue('time', promoAutomation?.schedule?.time ?? defaultSchedule.time);
    setValue('days', promoAutomation?.schedule?.days ?? defaultSchedule.days);
    setValue('enableAutomation', (promoAutomation?.status ?? 'active') === 'active');
    setValue('allowShareDuplicates', promoAutomation?.allow_share_duplicates ?? false);
    setValue('captionText', promoAutomation?.caption_text ?? '');
    setValue('templates', promoAutomation?.templates ?? [newEmptyTemplate]);
    setValue('entity_id', promoAutomation?.entity_id ?? null);
    setValue(
      'automaticallyShareToNetworks',
      promoAutomation?.automatically_share_to_networks ?? true,
    );
    setValue('nickname', promoAutomation?.nickname ?? '');
    setInstagramSubNetworks(networks);
    setFacebookSubnetworks(networks);
    setYoutubeSubnetworks(networks);
    setValue('textOnly', promoAutomation?.text_only ?? false);
    setValue('title', promoAutomation?.title ?? '');
    setValue('filteredCategoryUrl', promoAutomation?.filtered_category_url ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoAutomation]);

  useEffect(() => {
    if (automationEventTypeSelected === PromoAutomationEventType.CRON_SOCIAL_NETWORK) {
      setValue('automaticallyShareToNetworks', true);
      setFilteredOtherDestinations([PromoAutomationOtherDestination.EMAIL]);
    }

    if (automationEventTypeSelected === PromoAutomationEventType.EVERY_LISTING_PUBLICATION) {
      setFilteredOtherDestinations([
        PromoAutomationOtherDestination.EMAIL,
        PromoAutomationOtherDestination.FILES,
      ]);
      setValue(
        'otherDestinations',
        _.uniq([...otherDestinations, PromoAutomationOtherDestination.FILES]),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationEventTypeSelected, PromoAutomationEventType]);

  useEffect(() => {
    const updatedTemplates = templates.map((template) => {
      const globalPromoMediaType = filteredGlobalPromoMediaTypes.find(
        (type) => type.id === template.global_promo_media_type_id,
      );
      return {
        ...template,
        global_promo_media_type_id:
          globalPromoMediaType?.id ?? filteredGlobalPromoMediaTypes[0]?.id,
      };
    });
    setValue('templates', updatedTemplates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGlobalPromoMediaTypes]);

  useEffect(() => {
    let isExclusiveVideoPostType = false;
    if (selectedNetworks.includes('instagram') && instagramPostType !== '') {
      const instagramPostTypeObj = INSTAGRAM_POST_NETWORK_TYPES.find(
        (type) => type.label === instagramPostType,
      );
      if (instagramPostTypeObj && _.isEqual(instagramPostTypeObj.mediaTypes, ['video'])) {
        isExclusiveVideoPostType = true;
      }
    }

    if (selectedNetworks.includes('facebook') && facebookPostType !== '') {
      const facebookPostTypeObj = FACEBOOK_POST_NETWORK_TYPES.find(
        (type) => type.label === facebookPostType,
      );
      if (facebookPostTypeObj && _.isEqual(facebookPostTypeObj.mediaTypes, ['video'])) {
        isExclusiveVideoPostType = true;
      }
    }

    if (selectedNetworks.includes('youtube') && youtubePostType !== '') {
      const youtubePostTypeObj = YOUTUBE_POST_NETWORK_TYPES.find(
        (type) => type.label === youtubePostType,
      );
      if (youtubePostTypeObj && _.isEqual(youtubePostTypeObj.mediaTypes, ['video'])) {
        isExclusiveVideoPostType = true;
      }
    }

    if (isExclusiveVideoPostType) {
      setValue('textOnly', false);
    }
    setIsVideoPostType(isExclusiveVideoPostType);

    setFilteredGlobalPromoMediaTypes(
      globalPromoMediaTypes.filter((globalPromoMediaType) => {
        if (automationEventTypeSelected === PromoAutomationEventType.EVERY_LISTING_PUBLICATION) {
          if (isExclusiveVideoPostType) {
            return (
              globalPromoMediaType.media_type === 'video' &&
              globalPromoMediaType.entity_type_name === 'Listing'
            );
          } else {
            return globalPromoMediaType.entity_type_name === 'Listing';
          }
        }

        if (isExclusiveVideoPostType) {
          return (
            globalPromoMediaType.media_type === 'video' &&
            globalPromoMediaType.entity_type_name === 'Category'
          );
        } else {
          return globalPromoMediaType.entity_type_name === 'Category';
        }
      }),
    );
  }, [
    automationEventTypeSelected,
    globalPromoMediaTypes,
    selectedNetworks,
    instagramPostType,
    facebookPostType,
    youtubePostType,
  ]);

  useEffect(() => {
    if (selectedNetworks.includes('instagram') && instagramPostType === '') {
      setInstagramPostType('stories');
    }

    if (selectedNetworks.includes('facebook') && facebookPostType === '') {
      setFacebookPostType('post');
    }

    if (selectedNetworks.includes('youtube') && youtubePostType === '') {
      setYoutubePostType('post');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetworks]);

  useEffect(() => {
    setCustomSchedule(!daysSelected.includes(DAILY_OPTION));
  }, [daysSelected]);

  useEffect(() => {
    if (!textOnly && templates.length === 0) {
      setValue('templates', [newEmptyTemplate]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textOnly]);

  const validateData = (data: PromoAutomationFields): boolean => {
    if (!data.entity_id) {
      toast({
        title: 'Please select a category',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (
      data.automationEventType === PromoAutomationEventType.CRON_SOCIAL_NETWORK &&
      data.days?.length === 0
    ) {
      toast({
        title: 'Please select a schedule',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (
      data.automaticallyShareToNetworks &&
      data.networks?.length === 0 &&
      data.otherDestinations?.length === 0
    ) {
      toast({
        title: 'Please select at least one network to share to or other destination',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    const templates = data.textOnly ? [] : data.templates;

    if (templates.some((t) => !t.global_promo_media_type_id && !t.use_static_image)) {
      toast({
        title: 'Please select a Media Template for each template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (templates.some((t) => t.use_static_image && !t.promo_image_url)) {
      toast({
        title: 'Please upload an image to use as static image for Promo Automation',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const buildPayload = (
    data: PromoAutomationFields,
    template?: Partial<PromoAutomationTemplate>,
  ) => {
    const networks = [
      ...data.networks.filter((network) => !network.match(/instagram|facebook|youtube/)),
      ...(data.networks.includes('instagram') && instagramPostType !== ''
        ? [`instagram:${instagramPostType}`]
        : []),
      ...(data.networks.includes('facebook') && facebookPostType !== ''
        ? [`facebook:${facebookPostType}`]
        : []),
      ...(data.networks.includes('youtube') && youtubePostType !== ''
        ? [`youtube:${youtubePostType}`]
        : []),
    ];

    const otherDestinations =
      data.automationEventType === PromoAutomationEventType.CRON_SOCIAL_NETWORK
        ? data.otherDestinations.filter(
            (destination) => destination !== PromoAutomationOtherDestination.FILES,
          )
        : _.uniq([...data.otherDestinations, PromoAutomationOtherDestination.FILES]);

    return {
      ..._.omit(promoAutomation, [
        'entity_type_object',
        'entity_type_name',
        'global_promo_media_type',
      ]),
      networks: data.automaticallyShareToNetworks ? networks : [],
      other_destinations: otherDestinations,
      schedule: { days: data.days, time: data.time },
      status: data.enableAutomation ? 'active' : 'disabled',
      automation_event_type: data.automationEventType,
      allow_share_duplicates: data.allowShareDuplicates,
      caption_text: data.captionText,
      automatically_share_to_networks:
        data.automationEventType === PromoAutomationEventType.CRON_SOCIAL_NETWORK
          ? true
          : data.automaticallyShareToNetworks,
      templates: templates,
      entity_id: data.entity_id,
      nickname: data.nickname,
      text_only: data.textOnly,
      title: data.title,
      filtered_category_url: data.filteredCategoryUrl,
    };
  };

  const decorateBeforeSubmitData = (data: PromoAutomationFields) => {
    const isValidData = validateData(data);
    if (!isValidData) {
      return;
    }

    const payload = buildPayload(data) as Partial<PromoAutomation>;

    if (promoAutomation?.id) {
      onUpdatePromoAutomation(payload);
    } else {
      onCreatePromoAutomation(payload);
    }
  };

  const previewPromoMedia = async (template?: Partial<PromoAutomationTemplate>) => {
    if (!marketplace || (!entityId && !filteredCategoryUrl)) return;

    const data = getValues();
    const isValidData = validateData(data);
    if (!isValidData) return;

    setShowPreviewModal(true);

    const payload = buildPayload(data, template);
    if (entityId) {
      try {
        const response = await api.shareautomation.promoAutomations.createPreview(
          marketplace.id,
          entityId,
          payload,
        );
        setPreviewPromoMediaData(response);
      } catch (err) {
        console.error(err);
        toast({
          title: 'Error',
          description:
            err.message ?? 'Failed to preview promo media. Please review the form and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        closePreviewModal();
      }
    } else {
      toast({
        title: 'Error',
        description: 'Unable to preview promo media without a selected category.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      closePreviewModal();
    }
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewPromoMediaData(null);
  };

  const updateTemplate = (index: number, template: Partial<PromoAutomationTemplate>) => {
    const updatedTemplates = [...templates];
    updatedTemplates[index] = template;
    setValue('templates', updatedTemplates);
  };

  const addNewTemplate = () => {
    setValue('templates', [...templates, newEmptyTemplate]);
  };

  const removeTemplate = (index: number) => {
    if (templates.length === 1) {
      return;
    }
    const updatedTemplates = templates.filter((_, i) => i !== index);
    setValue('templates', updatedTemplates);
  };

  return (
    <Flex
      direction="column"
      ref={ref}
      width="100%"
      maxWidth="100%"
      className="promo-automation-form"
    >
      <Flex direction="row" alignItems="center" justifyContent="space-between">
        <Text fontWeight="semibold" pb={4}>
          {promoAutomation?.id ? 'Edit' : 'New'} Promo Automation
        </Text>
      </Flex>
      <Stack as="form" onSubmit={handleSubmit(decorateBeforeSubmitData)} width="100%">
        <Flex gap={8} direction="column" width="100%">
          <Stack width="100%">
            <FormControl id="entity_type_name">
              <FormLabel>{_.capitalize(promoAutomation?.entity_type_name)}</FormLabel>
              {promoAutomation?.entity_type_object?.name && !isCategoryEditable ? (
                <Input type="text" value={promoAutomation?.entity_type_object.name} disabled />
              ) : (
                <>
                  <Flex gap={4} alignItems="flex-start" style={{ overflow: 'hidden' }}>
                    <FormControl flex="1" minWidth="0">
                      <FormLabel fontSize="sm">Choose Category</FormLabel>
                      <CategorySelector
                        mode="category-admin"
                        marketplaceId={marketplace?.id}
                        onCategorySelect={(category) => setValue('entity_id', category?.id ?? null)}
                        disabled={!!filteredCategoryUrl}
                        disabledReason={
                          !!filteredCategoryUrl
                            ? 'Category is locked to the parsed filtered URL'
                            : ''
                        }
                        selectedCategoryId={entityId ? entityId.toString() : undefined}
                      />
                    </FormControl>
                    <Flex alignItems="center" mt={8} flexShrink={0}>
                      <Text fontSize="sm" color="gray.500">
                        - OR -
                      </Text>
                    </Flex>
                    <FormControl id="filteredCategoryUrlInput" flex="1" minWidth="0">
                      <FormLabel htmlFor="filteredCategoryUrlInput" fontSize="sm">
                        Add Filtered Category URL
                      </FormLabel>
                      <Input
                        type="text"
                        value={filteredUrlInput}
                        onChange={(e) => setFilteredUrlInput(e.target.value)}
                        onPaste={(e) => {
                          const pastedText = e.clipboardData.getData('text');
                          setFilteredUrlInput(pastedText);
                          // Use setTimeout to ensure the state update happens first
                          setTimeout(() => parseAndApplyFilteredUrl(pastedText), 0);
                        }}
                        onBlur={() => parseAndApplyFilteredUrl(filteredUrlInput)}
                        placeholder="Paste a category URL with filters"
                      />
                      <FormHelperText fontSize="xs">
                        Paste a category URL with filters to lock the category and save the filters
                      </FormHelperText>
                    </FormControl>
                  </Flex>
                  {marketplace && entityId && filteredCategoryUrl && (
                    <Box mt={4} style={{ overflow: 'hidden', maxWidth: '100%' }}>
                      <Box
                        style={{
                          wordBreak: 'break-all',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Text fontWeight="semibold" color="gray.600" my={2}>
                          Filtered Category URL
                        </Text>
                        <Text
                          as="a"
                          href={`${getMarketplaceUrl(
                            marketplace,
                          )}/category/${entityId}?filters=${encodeURIComponent(
                            filteredCategoryUrl,
                          )}`}
                          target="_blank"
                          color="blue.500"
                          display="block"
                          cursor="pointer"
                          fontSize="sm"
                          _hover={{ textDecoration: 'underline' }}
                          style={{
                            wordBreak: 'break-all',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            display: 'block',
                          }}
                        >
                          {`${getMarketplaceUrl(
                            marketplace,
                          )}/category/${entityId}?filters=${filteredCategoryUrl}`}
                        </Text>
                      </Box>
                      <Flex direction="column" fontSize="sm" color="gray.600" mt={1}>
                        {(() => {
                          try {
                            const obj = JSON.parse(filteredCategoryUrl || '{}');
                            return Object.entries(obj)
                              .filter(
                                ([, v]) => v !== undefined && v !== null && String(v).length > 0,
                              )
                              .map(([k, v], index) => (
                                <Text key={index} fontSize="sm" color="gray.600" pl={2}>
                                  {`${_.startCase(k)}: ${v}`}
                                </Text>
                              ));
                          } catch (e) {
                            return null;
                          }
                        })()}
                      </Flex>
                    </Box>
                  )}
                </>
              )}
            </FormControl>
          </Stack>
          <Stack width="100%">
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <FormControl id="title">
                  <FormLabel htmlFor="title">
                    Title
                    <InfoTooltip description="Public facing, used for YouTube posts" />
                  </FormLabel>
                  <Input type="text" {...field} />
                  <FormHelperText>
                    You can use{' '}
                    <a style={{ textDecoration: 'underline' }} href="#dynamic-fields">
                      Dynamic Template Variables
                    </a>
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <Controller
              name="nickname"
              control={control}
              render={({ field }) => (
                <FormControl id="nickname">
                  <FormLabel>
                    Nickname
                    <InfoTooltip description="Not public facing, for internal use only" />
                  </FormLabel>
                  <Input type="text" {...field} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <Controller
              name="automationEventType"
              control={control}
              render={({ field }) => (
                <FormControl id="automationEventType" isRequired>
                  <FormLabel>Automation Event Type</FormLabel>
                  <Select {...field}>
                    <option value={PromoAutomationEventType.CRON_SOCIAL_NETWORK}>
                      {AutomationEventType.CRON_SOCIAL_NETWORK}
                    </option>
                    <option value={PromoAutomationEventType.EVERY_LISTING_PUBLICATION}>
                      {AutomationEventType.EVERY_LISTING_PUBLICATION}
                    </option>
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          {automationEventTypeSelected === PromoAutomationEventType.EVERY_LISTING_PUBLICATION && (
            <Stack width="100%">
              <Controller
                name="automaticallyShareToNetworks"
                control={control}
                render={({ field }) => (
                  <FormControl id="automaticallyShareToNetworks" display="flex" alignItems="center">
                    <FormLabel htmlFor="automaticallyShareToNetworks" m={0} mr={4}>
                      Automatically Share To Networks?
                    </FormLabel>
                    <Switch
                      size="md"
                      isChecked={field.value}
                      onBlur={field.onBlur}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                )}
              />
            </Stack>
          )}
          {(isSharingToNetworksAutomatically ||
            automationEventTypeSelected === PromoAutomationEventType.CRON_SOCIAL_NETWORK) && (
            <Controller
              name="networks"
              control={control}
              render={({ field }) => (
                <FormControl id="networks" display="flex" flexDirection="column" mb={4} gap={4}>
                  <FormLabel htmlFor="networks" m={0} mr={4}>
                    Networks
                  </FormLabel>
                  {!connectedSocialProfilesFetched ? (
                    <Flex alignItems="center" gap={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">
                        Loading connected networks...
                      </Text>
                    </Flex>
                  ) : connectedSocialProfiles.length > 0 ? (
                    <CheckboxGroup {...field}>
                      <Flex direction="column">
                        {connectedSocialProfiles.map((profile) => (
                          <Flex direction="column" key={profile.platform}>
                            <Checkbox
                              value={profile.platform}
                              disabled={
                                !MULTI_TEMPLATE_NETWORKS.includes(profile.platform) &&
                                templates.length > 1
                              }
                            >
                              {_.capitalize(profile.platform)}
                            </Checkbox>
                            {profile.platform === 'instagram' &&
                              selectedNetworks.includes('instagram') && (
                                <RadioGroup
                                  name="instagramPostType"
                                  value={instagramPostType}
                                  onChange={(value) => setInstagramPostType(value)}
                                >
                                  <Flex direction="column" ml={6}>
                                    {INSTAGRAM_POST_NETWORK_TYPES.map((type) => (
                                      <Radio key={type.label} value={type.label}>
                                        {_.capitalize(type.label)}
                                      </Radio>
                                    ))}
                                  </Flex>
                                </RadioGroup>
                              )}
                            {profile.platform === 'facebook' &&
                              selectedNetworks.includes('facebook') && (
                                <RadioGroup
                                  name="facebookPostType"
                                  value={facebookPostType}
                                  onChange={(value) => setFacebookPostType(value)}
                                >
                                  <Flex direction="column" ml={6}>
                                    {FACEBOOK_POST_NETWORK_TYPES.map((type) => (
                                      <Radio key={type.label} value={type.label}>
                                        {_.capitalize(type.label)}
                                      </Radio>
                                    ))}
                                  </Flex>
                                </RadioGroup>
                              )}
                            {profile.platform === 'youtube' &&
                              selectedNetworks.includes('youtube') && (
                                <RadioGroup
                                  name="youtubePostType"
                                  value={youtubePostType}
                                  onChange={(value) => setYoutubePostType(value)}
                                >
                                  <Flex direction="column" ml={6}>
                                    {YOUTUBE_POST_NETWORK_TYPES.map((type) => (
                                      <Radio key={type.label} value={type.label}>
                                        {_.capitalize(type.label)}
                                      </Radio>
                                    ))}
                                  </Flex>
                                </RadioGroup>
                              )}
                          </Flex>
                        ))}
                      </Flex>
                    </CheckboxGroup>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No networks connected. Please connect networks to Loma to enable sharing.
                    </Text>
                  )}
                </FormControl>
              )}
            />
          )}
          {connectedSocialProfilesFetched && !connectedSocialProfiles.length && (
            <Alert status="warning" borderRadius="lg">
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justifyContent="space-between"
                gap={4}
                alignItems="center"
                w="full"
              >
                <Flex direction="row" alignItems="center">
                  <AlertIcon />
                  <AlertDescription>No connected networks to Loma</AlertDescription>
                </Flex>
                <Flex>
                  <Button
                    colorScheme="orange"
                    variant="outline"
                    as={Link}
                    href={`/api/marketplace/${marketplace?.id}/manage_social_connected_accounts`}
                    target="_blank"
                  >
                    Connect networks to Loma
                  </Button>
                </Flex>
              </Flex>
            </Alert>
          )}
          <Controller
            name="otherDestinations"
            control={control}
            render={({ field }) => (
              <FormControl
                id="otherDestinations"
                display="flex"
                flexDirection="column"
                mb={4}
                gap={4}
              >
                <FormLabel htmlFor="otherDestinations">Other Destinations</FormLabel>
                <CheckboxGroup {...field}>
                  <Flex direction="column">
                    {filteredOtherDestinations.map((destination) => (
                      <Checkbox
                        key={destination}
                        value={destination}
                        isChecked={PromoAutomationOtherDestination.FILES === destination}
                        disabled={PromoAutomationOtherDestination.FILES === destination}
                      >
                        {destination}
                      </Checkbox>
                    ))}
                  </Flex>
                </CheckboxGroup>
              </FormControl>
            )}
          />
          <Stack>
            <Controller
              name="textOnly"
              control={control}
              render={({ field }) => (
                <FormControl id="textOnly" display="flex" alignItems="center">
                  <FormLabel htmlFor="textOnly" m={0} mr={4}>
                    Generate Image?
                    <InfoTooltip description="If not checked, generate a text-only post (for X, or other text-centric networks)" />
                  </FormLabel>
                  <Switch
                    size="md"
                    disabled={isVideoPostType}
                    isChecked={!field.value}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(!e.target.checked)}
                  />
                </FormControl>
              )}
            />
          </Stack>
          <Stack>
            <Controller
              name="enableAutomation"
              control={control}
              render={({ field }) => (
                <FormControl id="enableAutomation" display="flex" alignItems="center">
                  <FormLabel htmlFor="enableAutomation" m={0} mr={4}>
                    Enable Automation?
                  </FormLabel>
                  <Switch
                    size="md"
                    isChecked={field.value}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </FormControl>
              )}
            />
          </Stack>
          {isSharingToNetworksAutomatically && (
            <Stack>
              <Controller
                name="allowShareDuplicates"
                control={control}
                render={({ field }) => (
                  <FormControl id="allowShareDuplicates" display="flex" alignItems="center">
                    <FormLabel htmlFor="allowShareDuplicates" m={0} mr={4}>
                      Allow Share Duplicate Content?
                    </FormLabel>
                    <Switch
                      size="md"
                      isChecked={field.value}
                      onBlur={field.onBlur}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                )}
              />
            </Stack>
          )}
          <Stack>
            <Flex direction="column" mb={4}>
              <FormControl isRequired>
                <FormLabel m={0} mr={4}>
                  Schedule
                </FormLabel>
              </FormControl>
            </Flex>
            {automationEventTypeSelected === PromoAutomationEventType.EVERY_LISTING_PUBLICATION && (
              <Alert status="info">
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  justifyContent="space-between"
                  gap={4}
                  alignItems="center"
                  w="full"
                >
                  <Flex direction="row" alignItems="center">
                    <AlertIcon />
                    <AlertDescription>
                      Every time a new listing is published in this category, the automated media
                      will be published to the social channel(s).
                    </AlertDescription>
                  </Flex>
                </Flex>
              </Alert>
            )}
            {automationEventTypeSelected === PromoAutomationEventType.CRON_SOCIAL_NETWORK && (
              <>
                <Controller
                  name="days"
                  control={control}
                  render={({ field }) => (
                    <FormControl id="schedule" display="flex" flexDirection="column" mb={4} gap={4}>
                      <RadioGroup
                        name="schedule"
                        value={customSchedule ? 'Custom Schedule' : (daysSelected ?? [])[0]}
                        onChange={(value) => {
                          if (value === 'Custom Schedule') {
                            setCustomSchedule(true);
                            setValue('days', []);
                          } else {
                            setCustomSchedule(false);
                            setValue('days', [value]);
                          }
                        }}
                      >
                        <Flex direction="column">
                          <Radio value={DAILY_OPTION} ml={{ base: 0, md: '0.5rem' }} mt={2}>
                            {DAILY_OPTION}
                          </Radio>
                          <Radio value="Custom Schedule" ml={{ base: 0, md: '0.5rem' }} mt={2}>
                            Custom Schedule
                          </Radio>
                        </Flex>
                      </RadioGroup>
                      {customSchedule && (
                        <CheckboxGroup {...field}>
                          <Flex direction="column" ml={6}>
                            {DAYS_OPTIONS.map((schedule) => (
                              <Checkbox key={schedule} value={schedule} name="schedule">
                                {schedule}
                              </Checkbox>
                            ))}
                          </Flex>
                        </CheckboxGroup>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      id="time"
                      display="flex"
                      flexDirection="column"
                      mb={4}
                      gap={4}
                      w="fit-content"
                    >
                      <Select {...field}>
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </>
            )}
          </Stack>
          <Stack>
            <Controller
              name="captionText"
              control={control}
              render={({ field }) => (
                <FormControl
                  id="captionText"
                  display="flex"
                  flexDirection="column"
                  mb={4}
                  isRequired={textOnly}
                >
                  <FormLabel htmlFor="captionText">Caption Text</FormLabel>
                  <Textarea {...field} />
                  <FormHelperText>
                    You can use{' '}
                    <a style={{ textDecoration: 'underline' }} href="#dynamic-fields">
                      Dynamic Template Variables
                    </a>
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
          <DynamicFieldsGuide
            namespaceMapping={globalPromoMediaTypeNamespaceMapping}
            id="dynamic-fields"
          />
          {textOnly && (
            <Flex
              direction="row"
              alignItems="center"
              gap={4}
              justifyContent="flex-start"
              flexGrow="1"
            >
              <Button colorScheme="secondary" type="button" onClick={() => previewPromoMedia()}>
                Preview
              </Button>
            </Flex>
          )}
          <Flex direction="row" alignItems="center" gap={4} justifyContent="flex-end" flexGrow="1">
            {isMultiTemplate && !textOnly && (
              <Button type="button" colorScheme="secondary" onClick={addNewTemplate}>
                Add new template
              </Button>
            )}
          </Flex>
          {!textOnly && (
            <Accordion defaultIndex={[0]} allowMultiple>
              {templates.map((template, index) => (
                <AccordionItem key={index}>
                  <AccordionButton>
                    <Box as="span" textAlign={'left'} flex={1}>
                      <h2>Template {index + 1}</h2>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <PromoAutomationTemplateForm
                      canRemoveTemplate={templates.length > 1}
                      template={template}
                      globalPromoMediaTypeNamespaceMapping={globalPromoMediaTypeNamespaceMapping}
                      globalPromoMediaTypes={filteredGlobalPromoMediaTypes}
                      removeTemplate={() => removeTemplate(index)}
                      updateTemplateValues={(template: Partial<PromoAutomationTemplate>) =>
                        updateTemplate(index, template)
                      }
                      previewPromoMedia={() => previewPromoMedia(template)}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          <Flex direction="row" alignItems="center">
            <Flex
              direction="row"
              alignItems="center"
              gap={4}
              justifyContent="flex-end"
              flexGrow="1"
            >
              <Button type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="primary" type="submit">
                Save
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Stack>
      <Modal onClose={closePreviewModal} isOpen={showPreviewModal} isCentered>
        <ModalOverlay />
        <ModalContent minH="500px">
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody display="flex">
            <Flex
              alignItems="center"
              direction="column"
              justifyContent="center"
              height="100%"
              margin="auto"
            >
              {!previewPromoMediaData ? (
                <Flex
                  alignItems="center"
                  direction="column"
                  gap={4}
                  justifyContent="center"
                  height="100%"
                  margin="auto"
                >
                  <Text fontWeight="semibold">
                    Most recent Listings in Category will be used to generate preview...
                  </Text>
                  <Spinner />
                </Flex>
              ) : (
                <Flex direction="column" gap={4}>
                  <Text whiteSpace={'pre-wrap'} fontWeight={'semibold'}>
                    {previewPromoMediaData.caption}
                  </Text>
                  {previewPromoMediaData.image_url && (
                    <Image
                      src={previewPromoMediaData.image_url}
                      alt="Promo Media"
                      maxH="500px"
                      w="auto"
                    />
                  )}
                </Flex>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
});

export default PromoAutomationForm;
